import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Smartphone, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { handleApiError } from '@/lib/api/axios'
import { credentialToServerFormat, isWebAuthnSupported, registerEnterprisePasskey } from '@/lib/security/webauthn-enterprise'
import { devicesApi, deviceKeys, type Device } from '../api'
import { DeviceRow } from './DeviceRow'
import { StepUpDialog } from './StepUpDialog'

type Pending = { kind: 'add-passkey' } | { kind: 'add-totp' } | { kind: 'remove'; device: Device }

/**
 * My devices: the signed-in user's own sign-in devices (MFA build plan, Stage 1).
 *
 * Looking needs only the session. Adding or removing needs a fresh proof of a device the user
 * already has - asked for once, good for five minutes, then asked for again. The last device has
 * no Remove: the server refuses it too.
 */
export function MyDevices() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { data: devices = [], isLoading, isError, error } = useQuery({ queryKey: deviceKeys.mine, queryFn: devicesApi.list })

    // The step-up token, held in memory only, with when it stops being good.
    const stepUp = useRef<{ token: string; until: number } | null>(null)
    const [pending, setPending] = useState<Pending | null>(null)
    const [askStepUp, setAskStepUp] = useState(false)
    const [active, setActive] = useState<Pending | null>(null)
    const [failure, setFailure] = useState<string | null>(null)

    const token = () => (stepUp.current && stepUp.current.until > Date.now() ? stepUp.current.token : null)
    const begin = (what: Pending) => {
        setFailure(null)
        if (token()) setActive(what)
        else { setPending(what); setAskStepUp(true) }
    }
    const refresh = () => queryClient.invalidateQueries({ queryKey: deviceKeys.mine })
    const finish = () => { setActive(null); refresh() }
    // The server said the proof has run out (or was never there): ask again, then carry on.
    const onStepUpRequired = (what: Pending) => { stepUp.current = null; setActive(null); setPending(what); setAskStepUp(true) }

    const remove = useMutation({
        mutationFn: (device: Device) => devicesApi.remove(token() ?? '', device.id),
        onSuccess: finish,
        onError: (e, device) => {
            if (isStepUpRequired(e)) onStepUpRequired({ kind: 'remove', device })
            else setFailure(handleApiError(e))
        },
    })

    if (isLoading) return <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>
    if (isError) return <p className="text-sm text-destructive" role="alert">{handleApiError(error)}</p>

    return (
        <div className="space-y-4" data-testid="my-devices">
            {devices.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="no-devices">{t('devices.none')}</p>
            ) : (
                <ul className="space-y-2">
                    {devices.map((device) => (
                        <DeviceRow
                            key={device.id}
                            device={device}
                            action={devices.length > 1 ? (
                                <Button
                                    variant="outline" size="sm"
                                    aria-label={t('devices.removeNamed', { name: device.name })}
                                    onClick={() => begin({ kind: 'remove', device })}
                                >
                                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                                    {t('devices.remove')}
                                </Button>
                            ) : (
                                <span className="text-xs text-muted-foreground" data-testid="only-device">{t('devices.onlyDevice')}</span>
                            )}
                        />
                    ))}
                </ul>
            )}

            {/* Adding a device needs one to prove yourself with: with none, an administrator starts it. */}
            {devices.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {isWebAuthnSupported() && (
                        <Button variant="outline" size="sm" onClick={() => begin({ kind: 'add-passkey' })}>
                            <KeyRound className="mr-1 h-3.5 w-3.5" />
                            {t('devices.addPasskey')}
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => begin({ kind: 'add-totp' })}>
                        <Smartphone className="mr-1 h-3.5 w-3.5" />
                        {t('devices.addTotp')}
                    </Button>
                </div>
            )}
            {failure && <p className="text-sm text-destructive" role="alert">{failure}</p>}

            <StepUpDialog
                open={askStepUp}
                devices={devices}
                onClose={() => { setAskStepUp(false); setPending(null) }}
                onSteppedUp={(newToken, seconds) => {
                    // A little short of the server's five minutes, so a token is never sent as it expires.
                    stepUp.current = { token: newToken, until: Date.now() + Math.max(0, seconds - 15) * 1000 }
                    setAskStepUp(false)
                    setActive(pending)
                    setPending(null)
                }}
            />

            {active?.kind === 'remove' && (
                <Dialog open onOpenChange={(next) => { if (!next) setActive(null) }}>
                    <DialogContent className="sm:max-w-md" data-testid="remove-device-dialog">
                        <DialogHeader>
                            <DialogTitle>{t('devices.removeTitle', { name: active.device.name })}</DialogTitle>
                            <DialogDescription>{t('devices.removeDescription')}</DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setActive(null)} disabled={remove.isPending}>{t('common.cancel')}</Button>
                            <Button variant="destructive" onClick={() => remove.mutate(active.device)} disabled={remove.isPending}>
                                {t('devices.remove')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
            {active?.kind === 'add-totp' && (
                <AddTotpDialog token={token() ?? ''} onClose={() => setActive(null)} onAdded={finish} onStepUpRequired={() => onStepUpRequired({ kind: 'add-totp' })} />
            )}
            {active?.kind === 'add-passkey' && (
                <AddPasskeyDialog token={token() ?? ''} onClose={() => setActive(null)} onAdded={finish} onStepUpRequired={() => onStepUpRequired({ kind: 'add-passkey' })} />
            )}
        </div>
    )
}

/** The server's "prove a device first" - as opposed to any other refusal. */
export function isStepUpRequired(error: unknown): boolean {
    const failed = error as { response?: { status?: number; data?: { error?: string; message?: string } }; config?: { stepUpToken?: unknown } }
    const data = failed?.response
    if (data?.status === 403 && JSON.stringify(data.data ?? {}).includes('step_up_required')) return true
    // The proof itself has run out: a 401 on a call that was sent on the step-up token.
    return data?.status === 401 && Boolean(failed?.config?.stepUpToken)
}

function AddTotpDialog({ token, onClose, onAdded, onStepUpRequired }: { token: string; onClose: () => void; onAdded: () => void; onStepUpRequired: () => void }) {
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [code, setCode] = useState('')
    const [failure, setFailure] = useState<string | null>(null)
    const fail = (e: unknown) => { if (isStepUpRequired(e)) onStepUpRequired(); else setFailure(handleApiError(e)) }
    // Nothing is stored by this: the secret becomes a device only when a code from it is confirmed.
    const setup = useQuery({ queryKey: ['devices', 'totp-setup', token], queryFn: () => devicesApi.totpBegin(token), staleTime: Infinity, gcTime: 0, retry: false })
    const confirm = useMutation({
        mutationFn: () => devicesApi.totpConfirm(token, { code, setupTicket: setup.data!.setupTicket, name: name.trim() || undefined }),
        onSuccess: onAdded,
        onError: fail,
    })
    const setupNeedsStepUp = setup.isError && isStepUpRequired(setup.error)
    useEffect(() => { if (setupNeedsStepUp) onStepUpRequired() }, [setupNeedsStepUp]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
            <DialogContent className="sm:max-w-md" data-testid="add-totp-dialog">
                <DialogHeader>
                    <DialogTitle>{t('devices.addTotp')}</DialogTitle>
                    <DialogDescription>{t('devices.totp.description')}</DialogDescription>
                </DialogHeader>
                {setup.isLoading && <p className="text-sm text-muted-foreground">{t('devices.loading')}</p>}
                {setup.isError && !isStepUpRequired(setup.error) && <p className="text-sm text-destructive" role="alert">{handleApiError(setup.error)}</p>}
                {setup.data && (
                    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (/^\d{6}$/.test(code)) confirm.mutate() }}>
                        <div className="flex justify-center"><img src={setup.data.qrCode} alt={t('devices.totp.qrAlt')} className="h-48 w-48" /></div>
                        <p className="break-all text-center font-mono text-xs text-muted-foreground">{setup.data.secret}</p>
                        <div className="space-y-1">
                            <Label htmlFor="totp-name">{t('devices.nameLabel')}</Label>
                            <Input id="totp-name" value={name} maxLength={120} placeholder={t('devices.totp.namePlaceholder')} onChange={(e) => setName(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="totp-code">{t('devices.totp.codeLabel')}</Label>
                            <Input id="totp-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                        </div>
                        {failure && <p className="text-sm text-destructive" role="alert">{failure}</p>}
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={onClose} disabled={confirm.isPending}>{t('common.cancel')}</Button>
                            <Button type="submit" disabled={confirm.isPending || !/^\d{6}$/.test(code)}>{t('devices.add')}</Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    )
}

function AddPasskeyDialog({ token, onClose, onAdded, onStepUpRequired }: { token: string; onClose: () => void; onAdded: () => void; onStepUpRequired: () => void }) {
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [failure, setFailure] = useState<string | null>(null)
    const add = useMutation({
        mutationFn: async () => {
            const options = await devicesApi.passkeyBegin(token)
            const { credential, deviceName } = await registerEnterprisePasskey(options, name.trim() || undefined)
            return devicesApi.passkeyComplete(token, { credential: credentialToServerFormat(credential), name: name.trim() || deviceName })
        },
        onSuccess: onAdded,
        onError: (e) => { if (isStepUpRequired(e)) onStepUpRequired(); else setFailure(handleApiError(e)) },
    })
    return (
        <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
            <DialogContent className="sm:max-w-md" data-testid="add-passkey-dialog">
                <DialogHeader>
                    <DialogTitle>{t('devices.addPasskey')}</DialogTitle>
                    <DialogDescription>{t('devices.passkey.description')}</DialogDescription>
                </DialogHeader>
                <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
                    <div className="space-y-1">
                        <Label htmlFor="passkey-name">{t('devices.nameLabel')}</Label>
                        <Input id="passkey-name" value={name} maxLength={120} placeholder={t('devices.passkey.namePlaceholder')} onChange={(e) => setName(e.target.value)} />
                    </div>
                    {failure && <p className="text-sm text-destructive" role="alert">{failure}</p>}
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose} disabled={add.isPending}>{t('common.cancel')}</Button>
                        <Button type="submit" disabled={add.isPending}>{t('devices.add')}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
